import { isPast, isToday } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useError } from '@shared/hooks/useError';
import { reportAppError } from '@shared/reportAppError';
import { toISODateFormat } from '@shared/utils/dateFormatters';
import { useRestaurant } from '../../../context/useRestaurant';
import * as reservationsApiHelpers from '../../apiHelpers';
import * as serviceApiHelpers from '../apiHelpers';
import { useReservationServiceContext } from '../state/ReservationServiceContext';
import { Closure } from '../../apiHelpers';
import { successToast } from '@components/toasts/Toasts';

export interface BlockReservationsArgs {
  startDate: string;
  endDate: string;
  firstReservationTime?: string | null;
  lastReservationTime?: string | null;
  guestFacingReason?: string;
}

export interface BlockRestaurantHookReturn {
  canBlockReservations: boolean;
  canUnblockReservations: boolean;
  blockReservations: (args: BlockReservationsArgs) => Promise<void>;
  unblockReservations: () => Promise<void>;
  reservationDate: Date;
  closures: Closure[];
}

export const useBlockReservations = (): BlockRestaurantHookReturn => {
  const { id: restaurantId } = useRestaurant();
  const { selectedDate } = useReservationServiceContext();
  const [closures, setClosures] = useState<Closure[]>([]);
  const setError = useError();

  const [areReservationsBlocked, setAreReservationsBlocked] = useState(false);
  const dateToFetch = toISODateFormat(selectedDate);

  useEffect(() => {
    const checkReservationsAreBlocked = async () => {
      try {
        const closures = await reservationsApiHelpers.getClosures(
          restaurantId,
          dateToFetch,
        );
        setAreReservationsBlocked(closures.length > 0);
        setClosures(closures);
      } catch (e) {
        setError(e);
      }
    };

    void checkReservationsAreBlocked();
  }, [restaurantId, dateToFetch, areReservationsBlocked]);

  const blockReservations = useCallback(
    async (args: BlockReservationsArgs) => {
      try {
        await serviceApiHelpers.blockReservations({
          restaurantId,
          ...args,
        });
        successToast({ message: 'Reservations blocked successfully!' });
        setAreReservationsBlocked(true);
      } catch (error) {
        reportAppError(error);
        setError(error);
      }
    },
    [restaurantId],
  );

  const unblockReservations = useCallback(async () => {
    try {
      const activeClosure = closures[0];
      if (!activeClosure) return;
      await serviceApiHelpers.unblockReservations(
        restaurantId,
        activeClosure.id,
      );
      successToast({ message: 'Reservations unblocked successfully!' });
      setAreReservationsBlocked(false);
    } catch (error) {
      reportAppError(error);
      setError(error);
    }
  }, [restaurantId, closures]);

  const reservationDateHasNotPassed =
    !isPast(selectedDate) || isToday(selectedDate);

  const canBlockReservations =
    reservationDateHasNotPassed && !areReservationsBlocked;
  const canUnblockReservations =
    reservationDateHasNotPassed && areReservationsBlocked;

  return {
    canBlockReservations,
    canUnblockReservations,
    blockReservations,
    unblockReservations,
    reservationDate: selectedDate,
    closures,
  };
};
